import { axios, AxiosResponse } from "@sdk/axios";
import { EntityServices } from "@sdk/utils/entity.service";
import type { PaginatedOptions, PaginatedResults } from "@sdk/utils/paginated-results";
import type { iBooking, iPaymentScheduleItem } from "@sdk/bookings/booking-model";
import type { iSupplierBill } from "./supplier-bill-model";
import type {
  iSupplierBillPaymentReceipt,
  SupplierBillPaymentReceiptCreateInput,
} from "@sdk/supplier-bill-payment-receipts/supplier-bill-payment-receipt-model";
import { postAccountingListReport } from "@sdk/accounting/accounting-list-report.service";
import type { SupplierBillListRow } from "@sdk/accounting/accounting-list-report.models";
import type { AccountingBulkOperationResult } from "@sdk/accounting-bulk-operations/accounting-bulk-operation-model";

export type BulkPayBillAllocation = {
  supplierBillId: string;
  amount: number;
};

export type BulkPaySupplierBillsParams = {
  supplierBillIds?: string[];
  allocations?: BulkPayBillAllocation[];
  paymentMethodId: string;
  paymentDate?: number;
};

export type BulkPaySupplierBillsResult = AccountingBulkOperationResult & {
  operationId: string;
  receipts: iSupplierBillPaymentReceipt[];
};

export class SupplierBillsServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
    this.supplierBills.configure(config);
  }

  constructor(protected config: { basePath: string; token: string }) {
    this.supplierBills = Object.assign(
      new EntityServices<iSupplierBill>(config, "supplier-bills", {}),
      {
        updatePaymentSchedule: async (
          bookingId: string,
          supplierBillId: string,
          body: { paymentSchedule: iPaymentScheduleItem[] }
        ): Promise<{ booking: iBooking; supplierBill: iSupplierBill }> => {
          const results = await axios.patch<
            { booking: iBooking; supplierBill: iSupplierBill }
          >(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-schedule`,
            body,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        addPaymentReceipt: async (
          bookingId: string,
          supplierBillId: string,
          receipt: SupplierBillPaymentReceiptCreateInput
        ): Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }> => {
          const results = await axios.post<
            { booking: iBooking; receipt: iSupplierBillPaymentReceipt }
          >(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-receipts`,
            receipt,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        previewPaymentReceiptJournals: async (
          bookingId: string,
          supplierBillId: string,
          receipt: SupplierBillPaymentReceiptCreateInput
        ) => {
          const results = await axios.post<
            import("@sdk/payment-receipt-journal-preview-model").PaymentReceiptJournalPreviewResult
          >(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-receipts/preview-journals`,
            receipt,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        updatePaymentReceiptRemark: async (
          bookingId: string,
          supplierBillId: string,
          receiptId: string,
          body: {
            remark?: string;
            remarkAttachments?: string[];
            paymentScheduleItemId?: string;
          }
        ): Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }> => {
          const results = await axios.patch<
            { booking: iBooking; receipt: iSupplierBillPaymentReceipt }
          >(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-receipts/${receiptId}/remark`,
            body,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        voidPaymentReceipt: async (
          bookingId: string,
          supplierBillId: string,
          receiptId: string
        ): Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }> => {
          const results = await axios.patch<
            { booking: iBooking; receipt: iSupplierBillPaymentReceipt }
          >(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-receipts/${receiptId}/void`,
            {},
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        removePaymentReceipt: async (
          bookingId: string,
          supplierBillId: string,
          receiptId: string
        ): Promise<{ booking: iBooking }> => {
          const results = await axios.delete<{ booking: iBooking }>(
            this.config.basePath +
              `/bookings/${bookingId}/supplier-bills/${supplierBillId}/payment-receipts/${receiptId}`,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
        report: (req: {
          query: Record<string, unknown>;
          options?: PaginatedOptions;
        }) =>
          postAccountingListReport<SupplierBillListRow>(
            config,
            "supplier-bills",
            req
          ),
        bulkPay: async (
          body: BulkPaySupplierBillsParams
        ): Promise<BulkPaySupplierBillsResult> => {
          const results = await axios.post<BulkPaySupplierBillsResult>(
            this.config.basePath + `/supplier-bills/bulk-pay`,
            body,
            {
              headers: { Authorization: this.config.token },
            }
          );
          return results.data;
        },
      }
    );
    this.supplierBills.configure(config);
  }

  supplierBills!: EntityServices<iSupplierBill> & {
    updatePaymentSchedule: (
      bookingId: string,
      supplierBillId: string,
      body: { paymentSchedule: iPaymentScheduleItem[] }
    ) => Promise<{ booking: iBooking; supplierBill: iSupplierBill }>;
    addPaymentReceipt: (
      bookingId: string,
      supplierBillId: string,
      receipt: SupplierBillPaymentReceiptCreateInput
    ) => Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }>;
    previewPaymentReceiptJournals: (
      bookingId: string,
      supplierBillId: string,
      receipt: SupplierBillPaymentReceiptCreateInput
    ) => Promise<
      import("@sdk/payment-receipt-journal-preview-model").PaymentReceiptJournalPreviewResult
    >;
    updatePaymentReceiptRemark: (
      bookingId: string,
      supplierBillId: string,
      receiptId: string,
      body: {
        remark?: string;
        remarkAttachments?: string[];
        paymentScheduleItemId?: string;
      }
    ) => Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }>;
    voidPaymentReceipt: (
      bookingId: string,
      supplierBillId: string,
      receiptId: string
    ) => Promise<{ booking: iBooking; receipt: iSupplierBillPaymentReceipt }>;
    removePaymentReceipt: (
      bookingId: string,
      supplierBillId: string,
      receiptId: string
    ) => Promise<{ booking: iBooking }>;
    report: (req: {
      query: Record<string, unknown>;
      options?: PaginatedOptions;
    }) => Promise<PaginatedResults<SupplierBillListRow>>;
    bulkPay: (
      body: BulkPaySupplierBillsParams
    ) => Promise<BulkPaySupplierBillsResult>;
  };
}
